import { patientPhoneToE164 } from '../../notification/standalone-patient-contact';

export type ColumnMapping = {
  subjectColumns: string[];
  phoneColumns: string[];
};

const PHONE_HEADER_HINTS = ['phone', 'mobile', 'cell', 'contact', 'number', 'tel'];
const NAME_HEADER_HINTS = ['subject', 'patient', 'name', 'first', 'last'];

function looksLikePhone(value: string): boolean {
  const digits = value.replace(/\D/g, '');
  if (digits.length < 10) {
    return false;
  }
  return patientPhoneToE164(value) !== null || digits.length >= 10;
}

function scorePhoneColumn(rows: string[][], colIndex: number): number {
  const sample = rows.slice(0, Math.min(rows.length, 25));
  if (sample.length === 0) {
    return 0;
  }
  let hits = 0;
  for (const row of sample) {
    if (looksLikePhone(row[colIndex] ?? '')) {
      hits += 1;
    }
  }
  return hits / sample.length;
}

function scoreNameColumn(rows: string[][], colIndex: number): number {
  const sample = rows.slice(0, Math.min(rows.length, 25));
  if (sample.length === 0) {
    return 0;
  }
  let hits = 0;
  for (const row of sample) {
    const v = (row[colIndex] ?? '').trim();
    if (!v || looksLikePhone(v)) {
      continue;
    }
    if (/[a-zA-Z]/.test(v)) {
      hits += 1;
    }
  }
  return hits / sample.length;
}

function headerHintScore(header: string, hints: string[]): number {
  const h = header.toLowerCase();
  return hints.some((hint) => h.includes(hint)) ? 0.15 : 0;
}

export function autoDetectColumnMapping(headers: string[], rows: string[][]): ColumnMapping {
  let bestPhoneIdx = -1;
  let bestPhoneScore = 0;
  headers.forEach((header, idx) => {
    const score = scorePhoneColumn(rows, idx) + headerHintScore(header, PHONE_HEADER_HINTS);
    if (score > bestPhoneScore) {
      bestPhoneScore = score;
      bestPhoneIdx = idx;
    }
  });

  let bestSubjectIdx = -1;
  let bestSubjectScore = 0;
  headers.forEach((header, idx) => {
    if (idx === bestPhoneIdx) {
      return;
    }
    const score = scoreNameColumn(rows, idx) + headerHintScore(header, NAME_HEADER_HINTS);
    if (score > bestSubjectScore) {
      bestSubjectScore = score;
      bestSubjectIdx = idx;
    }
  });

  const subjectColumns =
    bestSubjectIdx >= 0 && bestSubjectScore > 0.2 ? [headers[bestSubjectIdx]] : [];
  const phoneColumns = bestPhoneIdx >= 0 && bestPhoneScore > 0.2 ? [headers[bestPhoneIdx]] : [];

  return { subjectColumns, phoneColumns };
}

export function composeMappedRows(
  headers: string[],
  rows: string[][],
  mapping: ColumnMapping,
): { subject: string; phone: string }[] {
  const subjectIdx = mapping.subjectColumns.map((h) => headers.indexOf(h)).filter((i) => i >= 0);
  const phoneIdx = mapping.phoneColumns.map((h) => headers.indexOf(h)).filter((i) => i >= 0);

  return rows.map((row) => {
    const subject = subjectIdx
      .map((i) => (row[i] ?? '').trim())
      .filter(Boolean)
      .join(' ')
      .trim();
    const phoneRaw = phoneIdx.map((i) => (row[i] ?? '').trim()).join('');
    const phone = patientPhoneToE164(phoneRaw) ?? phoneRaw.trim();
    return { subject, phone };
  });
}
